import { jsx } from 'react/jsx-runtime';
import React__default, { useMemo } from 'react';
import { styled, css } from 'styled-components';
import { mergeRefs } from 'react-merge-refs';
import { clsx } from 'clsx';
import { generateContentPreview } from '../../../Tiptap/content.js';
import { TruncatorV2 } from '@fabric-app/ui/components/Truncator/TruncatorV2';
import { preventForwardPropsConfig } from '@fabric-app/ui/utils/preventForwardProps';
import { cssVar } from '@fabric-app/ui/theme/variables';

const EditorPreview = styled.div.attrs(({ data, limit = 750, style, isMobileCard, isListView, replaceClassName, simplifyTables = true, className, withTruncator = false, unstyled = false, ref: propRef, ...props }) => {
  const preview = useMemo(() => {
    const dataContent = "content" in data ? data.content : data;
    const content = typeof dataContent === "string" ? dataContent : "";
    const html = generateContentPreview(content, limit);
    if (!simplifyTables) return html;
    const tempElement = document.createElement("div");
    tempElement.innerHTML = html;
    const rows = tempElement.querySelectorAll("tr");
    for (let i = 0; i < rows.length; i++) {
      const cells = rows[i].querySelectorAll("td, th");
      const rowText = Array.from(cells).map((cell) => cell.textContent ? cell.textContent.trim() : "").join(" ");
      const newParagraph = document.createElement("div");
      newParagraph.textContent = rowText;
      rows[i].innerHTML = "";
      rows[i].appendChild(newParagraph);
    }
    return tempElement.innerHTML;
  }, [
    data,
    limit,
    simplifyTables
  ]);
  const ref = React__default.useRef(null);
  const mergedRef = useMemo(() => mergeRefs([
    ref,
    propRef
  ]), [
    ref,
    propRef
  ]);
  return {
    "data-mobile-card": isMobileCard,
    "data-preview-in-list": isListView,
    style,
    // backwards compatibility
    className: clsx(className, replaceClassName),
    ref: mergedRef,
    unstyled,
    ...props,
    children: withTruncator ? /* @__PURE__ */ jsx(TruncatorV2, {
      parentRef: ref,
      children: /* @__PURE__ */ jsx("div", {
        dangerouslySetInnerHTML: {
          __html: preview
        }
      })
    }) : /* @__PURE__ */ jsx("div", {
      dangerouslySetInnerHTML: {
        __html: preview
      }
    })
  };
}).withConfig(preventForwardPropsConfig([
  "withTruncator",
  "data",
  "simplifyTables",
  "isMobileCard",
  "unstyled"
])).withConfig({
  displayName: "EditorPreview",
  componentId: "sc-acb4d71-0"
})([
  `position:relative;word-wrap:break-word;white-space:pre-wrap;white-space:break-spaces;-webkit-font-variant-ligatures:none;font-variant-ligatures:none;font-feature-settings:'liga' 0;overflow:hidden;`,
  ``
], ({ unstyled }) => !unstyled && css([
  `h1[data-role='title']:first-child{display:none;}*{font-weight:normal;text-decoration:none;font-style:normal;color:`,
  `;pointer-events:none;font-size:13px;line-height:1.25rem;}&[data-mobile-card='true']{*{font-size:12px;line-height:1.5;}tr{th{div{max-width:110px;}}}}&[data-preview-in-list='true']{*{line-height:1.4;}}a{text-decoration:underline;pointer-events:none;}mark{background-color:transparent;color:inherit;}img{border-radius:4px;margin-block:8px;}ul,ol{margin:0;padding-left:1em;outline:none;list-style:disc;ul,ol{padding-left:1.4em;}ol ol{padding-left:1.4em;}ul ul{padding-left:1.4em;}}ol{list-style-type:none;counter-reset:item;padding-left:0;}ol li{counter-increment:item;display:grid;grid-template-columns:auto 1fr;align-items:flex-start;}ol li::before{content:counters(item,'.') '. ';grid-column:1;grid-row:1 / span 999;color:rgba(`,
  `,0.6);margin-right:0.3em;}ul > li{margin-left:6px;}ol ol{list-style-type:none;}ol ol li::before{content:counters(item,'.') '. ';}ul[data-type='taskList']{list-style:none;padding-left:0;li{display:flex;align-items:flex-start;gap:4px;margin-top:0.25rem;input{height:22px;margin-top:-3px;}input[checked] ~ div p{text-decoration:line-through rgba(`,
  `,0.5);}}ul{padding-left:1em;}}table{width:100%;border-collapse:collapse;table-layout:auto;display:block;overflow-x:visible;}td,th{padding:0.25rem 0.5rem;border:1px solid rgba(`,
  `,0.2);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;min-width:30px;}th{font-weight:600;}tr{height:2rem;display:table-row;}div[data-block='true']{> *:not(:first-child){margin-left:12px;}}div[data-type='fbc-toggle-list']{> *:not(:first-child){margin-left:12px;}&[data-expanded='false']{> *:not(:first-child){display:none;}}}ul[data-type='fbc-check-list']{list-style:none;padding-left:0;> li{margin-left:0;display:grid;grid-template-columns:auto 1fr;align-items:start;> label:first-child,> input:first-child{grid-column:1;grid-row:1 / span 999;margin-right:0.5em;}}ul{padding-left:1em;}}`
], cssVar["color-text-note-primary"], cssVar["color-text-note-primary-rgb"], cssVar["color-text-note-primary-rgb"], cssVar["color-text-note-primary-rgb"]));

export { EditorPreview as default };
//# sourceMappingURL=EditorPreview.js.map
